import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Alert,
  ButtonBase,
  Collapse,
  Paper,
  Typography,
  Chip,
  LinearProgress,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Tooltip,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Switch,
} from '@mui/material';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { formatDistanceToNow, differenceInCalendarDays, format } from 'date-fns';
import {
  FiberManualRecord as DotIcon,
  CheckCircle as ApprovedIcon,
  ErrorOutline as OverdueIcon,
  HourglassEmpty as InProgressIcon,
  Description as DraftIcon,
  CheckCircleOutline as ConnectedIcon,
  AccessTime as StaleIcon,
  ArrowForward as ArrowForwardIcon,
  Tune as TuneIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  RestartAlt as ResetIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { risk, workspace } from '../theme';
import PageHeader from '../components/PageHeader';
import { getActiveProjectId, subscribeProjectChange } from '../services/projectContext';

type WidgetId = 'attention' | 'deliveryRisk' | 'changed' | 'position' | 'health' | 'compliance' | 'categories' | 'sources' | 'upcoming' | 'risks' | 'activity';
type WidgetLayout = { id: WidgetId; w: number; h: number; visible: boolean; startExpanded: boolean };
const WIDGETS: { id: WidgetId; label: string; description: string }[] = [
  { id: 'attention', label: 'What needs attention', description: 'Overdue consents, review backlog and source freshness' },
  { id: 'deliveryRisk', label: 'Delivery risk', description: 'Delivery importance and deadline pressure' },
  { id: 'health', label: 'Compliance health', description: 'Health score and key measures' },
  { id: 'changed', label: 'What changed', description: 'Recent activity narrative' },
  { id: 'position', label: 'Project position', description: 'Project coverage summary' },
  { id: 'compliance', label: 'Compliance position', description: 'Status distribution' },
  { id: 'categories', label: 'Consents by category', description: 'Category mix' },
  { id: 'sources', label: 'Authority ingestion status', description: 'Source freshness' },
  { id: 'upcoming', label: 'Upcoming obligations', description: 'Next 30 days' },
  { id: 'risks', label: 'Overdue compliance obligations', description: 'Overdue consents' },
  { id: 'activity', label: 'Recent regulatory activity', description: 'Latest consent changes' },
];
const DEFAULT_EXPANDED: Record<WidgetId, boolean> = { attention: true, deliveryRisk: true, changed: false, position: false, health: true, compliance: true, categories: true, sources: true, upcoming: true, risks: true, activity: false };
const DEFAULT_LAYOUT: WidgetLayout[] = WIDGETS.map((w) => ({ id: w.id, w: ['attention','deliveryRisk','health'].includes(w.id) ? 12 : ['changed','position','upcoming','risks'].includes(w.id) ? 6 : 4, h: 1, visible: true, startExpanded: DEFAULT_EXPANDED[w.id] }));
const normalizeLayout = (saved: Partial<WidgetLayout>[]): WidgetLayout[] => {
  const known = new Set(saved.map((item) => item.id));
  return [...saved.filter((item) => WIDGETS.some((widget) => widget.id === item.id)).map(item => ({ ...DEFAULT_LAYOUT.find(defaultItem => defaultItem.id === item.id)!, ...item, startExpanded: item.startExpanded ?? DEFAULT_EXPANDED[item.id as WidgetId] })), ...DEFAULT_LAYOUT.filter((item) => !known.has(item.id))];
};

ChartJS.register(ArcElement, ChartTooltip, ChartLegend);

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const STATUS_META: Record<string, { label: string; color: string }> = {
  draft: { label: 'Draft', color: '#94A3B8' },
  submitted: { label: 'Submitted', color: '#2450C6' },
  in_review: { label: 'In Review', color: '#7C3AED' },
  approved: { label: 'Approved', color: '#16A34A' },
  rejected: { label: 'Rejected', color: '#DC2626' },
  withdrawn: { label: 'Withdrawn', color: '#6B7280' },
};

const MATRIX_URGENCY: { key: 'high' | 'medium' | 'low'; label: string; accent: string; ink: string; weight: number }[] = [
  { key: 'high', label: 'High', accent: risk.high, ink: '#7C2D12', weight: 1 },
  { key: 'medium', label: 'Medium', accent: risk.medium, ink: '#78350F', weight: 0.5 },
  { key: 'low', label: 'Low', accent: risk.low, ink: '#14532D', weight: 0 },
];

// Blends two hex colours for the matrix's continuous risk-zone gradient.
const mixHex = (from: string, to: string, t: number) => {
  const a = parseInt(from.slice(1), 16);
  const b = parseInt(to.slice(1), 16);
  const channel = (shift: number) => Math.round(((a >> shift) & 255) + (((b >> shift) & 255) - ((a >> shift) & 255)) * t);
  return `rgb(${channel(16)}, ${channel(8)}, ${channel(0)})`;
};
const matrixZoneColor = (t: number) => (t <= 0.5 ? mixHex(risk.low, risk.medium, t * 2) : mixHex(risk.medium, risk.high, (t - 0.5) * 2));

const ACTIVITY_ICON: Record<string, React.ReactNode> = {
  draft: <DraftIcon fontSize="small" sx={{ color: STATUS_META.draft.color }} />,
  submitted: <InProgressIcon fontSize="small" sx={{ color: STATUS_META.submitted.color }} />,
  in_review: <InProgressIcon fontSize="small" sx={{ color: STATUS_META.in_review.color }} />,
  approved: <ApprovedIcon fontSize="small" sx={{ color: STATUS_META.approved.color }} />,
  rejected: <OverdueIcon fontSize="small" sx={{ color: STATUS_META.rejected.color }} />,
  withdrawn: <DotIcon fontSize="small" sx={{ color: STATUS_META.withdrawn.color }} />,
};

interface ConnectorSummary {
  connector_id: string; authority_code: string; source_name: string; source_type: string;
  ingestion_mode: string; expected_cadence: string; active_record_count: number;
  pending_review_count: number; last_successful_run: { finished_at: string | null } | null;
}

interface DashboardData {
  total_consents: number;
  status_breakdown: Record<string, number>;
  category_breakdown: { code: string; name: string; color: string; count: number }[];
  overdue_count: number;
  upcoming_count: number;
  delivery_risk: { critical_overdue: number; high_due_30: number; critical_approved: number; points: { id: number; reference: string; title: string; criticality: number; urgency: string; days_to_target: number | null; status: string }[] };
  recent_consents: {
    id: number;
    reference: string;
    consent_type: string;
    status: string;
    status_percentage: number;
    updated_at: string;
  }[];
}

interface ConsentRow {
  id: number;
  reference: string;
  consent_type: { name: string; category: string } | null;
  cgb: { name: string } | null;
  status: string;
  target_approval_date: string | null;
  criticality: number;
}

const CLOSED_STATUSES = ['approved', 'rejected', 'withdrawn'];

function severityFor(days: number): { label: string; color: string } {
  if (days < 0) return { label: 'Critical', color: risk.critical };
  if (days <= 3) return { label: 'High', color: risk.high };
  return { label: 'Medium', color: risk.medium };
}

/** Compliance health score: 100 - overdue*25 - (upcoming-critical)*5, clamped 0..100 */
function calcHealthScore(overdue: number, upcoming: number, total: number): number {
  if (total === 0) return 100;
  const score = 100 - (overdue * 20) - (Math.max(0, upcoming - 2) * 4);
  return Math.max(0, Math.min(100, score));
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [consents, setConsents] = useState<ConsentRow[]>([]);
  const [connectors, setConnectors] = useState<ConnectorSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [customising, setCustomising] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [layout, setLayout] = useState<WidgetLayout[]>(DEFAULT_LAYOUT);
  const [draftLayout, setDraftLayout] = useState<WidgetLayout[]>(DEFAULT_LAYOUT);
  const [expanded, setExpanded] = useState<Record<WidgetId, boolean>>(DEFAULT_EXPANDED);
  const [layoutMenu, setLayoutMenu] = useState<{ id: WidgetId; anchor: HTMLElement } | null>(null);
  const [layoutError, setLayoutError] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<number | null>(getActiveProjectId());
  const [projectName, setProjectName] = useState('Immingham Green Energy Terminal (IGET)');

  useEffect(() => {
    const unsubscribe = subscribeProjectChange(project => { setProjectId(project.id); setProjectName(`${project.name} (${project.code})`); });
    fetch(`${API_BASE}/auth/session`).then(response => response.ok ? response.json() : null).then(data => {
      const selected = data?.user?.projects?.find((item: { id: number }) => item.id === getActiveProjectId());
      if (selected) setProjectName(`${selected.name} (${selected.code})`);
    }).catch(() => undefined);
    return unsubscribe;
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [dashRes, consentsRes, connectorsRes] = await Promise.all([
          fetch(`${API_BASE}/consents/dashboard${projectId ? `?project_id=${projectId}` : ''}`),
          fetch(`${API_BASE}/consents/?per_page=500${projectId ? `&project_id=${projectId}` : ''}`),
          fetch(`${API_BASE}/ingestion/connectors${projectId ? `?project_id=${projectId}` : ''}`),
        ]);
        setDashboard(await dashRes.json());
        const consentsData = await consentsRes.json();
        setConsents(consentsData.consents || []);
        if (connectorsRes.ok) setConnectors(await connectorsRes.json());
      } catch (err) {
        console.error('Failed to load overview:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    setLayout(DEFAULT_LAYOUT.map((item) => ({ ...item })));
    setExpanded({ ...DEFAULT_EXPANDED });
    fetch(`${API_BASE}/dashboard/preferences?project_id=${projectId}`)
      .then((response) => response.ok ? response.json() : null)
      .then((body) => { if (body?.preference?.layout?.length) { const saved = normalizeLayout(body.preference.layout); setLayout(saved); setExpanded(Object.fromEntries(saved.map(item => [item.id, item.startExpanded])) as Record<WidgetId, boolean>); } })
      .catch(() => undefined);
  }, [projectId]);

  const activeLayout = customising ? draftLayout : layout;
  const isVisible = (id: WidgetId) => activeLayout.find((item) => item.id === id)?.visible !== false;
  const orderFor = (id: WidgetId) => activeLayout.findIndex((item) => item.id === id);
  const widthFor = (id: WidgetId) => activeLayout.find((item) => item.id === id)?.w || 12;
  const enterCustomise = () => { setLayoutError(null); setDraftLayout(layout.map((item) => ({ ...item }))); setCustomising(true); };
  const cancelCustomise = () => { setDraftLayout(layout.map((item) => ({ ...item }))); setExpanded(Object.fromEntries(layout.map(item => [item.id, item.startExpanded])) as Record<WidgetId, boolean>); setLayoutError(null); setCustomising(false); };
  const saveLayout = async () => {
    if (!projectId) return;
    try {
      const response = await fetch(`${API_BASE}/dashboard/preferences?project_id=${projectId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ layout: draftLayout }) });
      if (!response.ok) throw new Error(`Unable to save dashboard layout (${response.status})`);
      setLayout(draftLayout); setExpanded(Object.fromEntries(draftLayout.map(item => [item.id, item.startExpanded])) as Record<WidgetId, boolean>); setLayoutError(null); setCustomising(false);
    } catch (error) {
      setLayoutError(error instanceof Error ? error.message : 'Unable to save dashboard layout');
    }
  };
  const resetLayout = () => { setDraftLayout(DEFAULT_LAYOUT.map((item) => ({ ...item }))); setExpanded({ ...DEFAULT_EXPANDED }); setLayoutError(null); };
  const toggleExpanded = (id: WidgetId) => setExpanded(items => ({ ...items, [id]: !items[id] }));
  const sectionHeader = (id: WidgetId, title: string, summary?: string) => (
    <Box role="button" tabIndex={0} onClick={() => toggleExpanded(id)} aria-expanded={expanded[id]} aria-label={`${expanded[id] ? 'Collapse' : 'Expand'} ${title}`} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); toggleExpanded(id); } }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', border: 0, bgcolor: 'transparent', p: 0, textAlign: 'left', cursor: 'pointer', color: 'inherit', '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 }, transition: 'margin 180ms ease, padding 180ms ease', '@media (prefers-reduced-motion: reduce)': { transition: 'none' } }}>
      <Stack direction="row" spacing={1} alignItems="center"><Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', alignItems: 'center', color: 'text.secondary' }}><ExpandMoreIcon sx={{ transform: expanded[id] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 260ms cubic-bezier(0.4, 0, 0.2, 1)', '@media (prefers-reduced-motion: reduce)': { transition: 'none' } }} /></Box><Box><Typography variant="h6" gutterBottom={expanded[id]}>{title}</Typography>{!expanded[id] && summary && <Typography variant="caption" color="text.secondary">{summary}</Typography>}</Box></Stack>
      {widgetControls(id)}
    </Box>
  );
  const updateWidget = (id: WidgetId, changes: Partial<WidgetLayout>) => setDraftLayout((items) => items.map((item) => item.id === id ? { ...item, ...changes } : item));
  const moveWidget = (id: WidgetId, direction: -1 | 1) => setDraftLayout((items) => { const index = items.findIndex((item) => item.id === id); const next = index + direction; if (index < 0 || next < 0 || next >= items.length) return items; const copy = items.slice(); [copy[index], copy[next]] = [copy[next], copy[index]]; return copy; });

  const today = useMemo(() => new Date(), []);

  const upcoming = useMemo(() => {
    return consents
      .filter((c) => c.target_approval_date && !CLOSED_STATUSES.includes(c.status))
      .map((c) => ({ ...c, days: differenceInCalendarDays(new Date(c.target_approval_date as string), today) }))
      .filter((c) => c.days >= 0 && c.days <= 30)
      .sort((a, b) => a.days - b.days)
      .slice(0, 8);
  }, [consents, today]);

  const risks = useMemo(() => {
    return consents
      .filter((c) => c.target_approval_date && !CLOSED_STATUSES.includes(c.status))
      .map((c) => ({ ...c, days: differenceInCalendarDays(new Date(c.target_approval_date as string), today) }))
      .filter((c) => c.days < 0)
      .sort((a, b) => a.days - b.days)
      .slice(0, 8);
  }, [consents, today]);

  const pendingReviewCount = connectors.reduce((total, connector) => total + connector.pending_review_count, 0);
  const staleSourceCount = connectors.filter((connector) => !connector.last_successful_run).length;

  if (loading || !dashboard) {
    return <LinearProgress />;
  }

  const healthScore = calcHealthScore(dashboard.overdue_count, dashboard.upcoming_count, dashboard.total_consents);
  const healthColor = healthScore >= 80 ? '#16A34A' : healthScore >= 60 ? '#D97706' : '#DC2626';
  const healthLabel = healthScore >= 80 ? 'Healthy' : healthScore >= 60 ? 'Caution' : 'At Risk';

  const donutData = {
    labels: Object.keys(dashboard.status_breakdown).map((k) => STATUS_META[k]?.label || k),
    datasets: [{
      data: Object.values(dashboard.status_breakdown),
      backgroundColor: Object.keys(dashboard.status_breakdown).map((k) => STATUS_META[k]?.color || '#94A3B8'),
      borderWidth: 0,
    }],
  };

  const maxCategoryCount = Math.max(...dashboard.category_breakdown.map((c) => c.count), 1);

  const matrixPoints = dashboard.delivery_risk?.points || [];
  const matrixCells: Record<string, number> = {};
  matrixPoints.forEach((point) => { const key = `${point.urgency}:${point.criticality}`; matrixCells[key] = (matrixCells[key] || 0) + 1; });
  const maxCellCount = Math.max(0, ...Object.values(matrixCells));
  const focusCount = matrixPoints.filter((point) => point.urgency === 'high' || (point.urgency === 'medium' && point.criticality >= 4)).length;
  const busiestCell = Object.entries(matrixCells).sort((a, b) => b[1] - a[1])[0];
  const criticalRiskCount = dashboard.delivery_risk?.critical_overdue || 0;
  const highRiskCount = dashboard.delivery_risk?.high_due_30 || 0;
  const onTrackCount = Math.max(0, dashboard.total_consents - criticalRiskCount - highRiskCount);

  const widgetControls = (id: WidgetId) => customising ? <>
    <Tooltip title="Widget options"><IconButton size="small" aria-label="Widget options" onClick={(event) => { event.stopPropagation(); setLayoutMenu({ id, anchor: event.currentTarget }); }}><TuneIcon fontSize="small" /></IconButton></Tooltip>
    {layoutMenu?.id === id && <Menu open anchorEl={layoutMenu.anchor} onClose={() => setLayoutMenu(null)}>
      <MenuItem onClick={() => { moveWidget(id, -1); setLayoutMenu(null); }}>Move up</MenuItem>
      <MenuItem onClick={() => { moveWidget(id, 1); setLayoutMenu(null); }}>Move down</MenuItem>
      <MenuItem onClick={() => { updateWidget(id, { w: widthFor(id) === 12 ? 6 : widthFor(id) === 6 ? 4 : 12 }); setLayoutMenu(null); }}>Resize widget</MenuItem>
      <MenuItem onClick={() => { updateWidget(id, { visible: false }); setLayoutMenu(null); }}>Hide widget</MenuItem>
    </Menu>}
  </> : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, bgcolor: customising ? workspace.customisingBackground : workspace.background, p: { xs: 0, md: 1 }, borderRadius: 2 }}>
      <PageHeader title={projectId ? "Project Overview" : "Organisation Portfolio"} subtitle={`${projectName} · ${format(today, 'dd MMMM yyyy')}`} action={!customising ? <Button size="small" variant="outlined" startIcon={<TuneIcon />} onClick={enterCustomise}>Customise dashboard</Button> : <Stack direction="row" spacing={1}><Button size="small" startIcon={<ResetIcon />} onClick={resetLayout}>Reset</Button><Button size="small" startIcon={<CloseIcon />} onClick={cancelCustomise}>Cancel</Button><Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={saveLayout}>Save layout</Button><Button size="small" variant="outlined" onClick={() => setDrawerOpen(true)}>Add widgets</Button></Stack>} />
      {layoutError && <Alert severity="error" sx={{ mb: 2 }}>{layoutError}</Alert>}
      {customising && <Paper sx={{ p: 1.5, mb: 2, order: -1, bgcolor: '#F8FAFC', borderColor: '#CBD5E1' }}>
        <Typography variant="body2" color="text.secondary">Arrange the workspace for this project. Changes are saved only when you choose Save layout.</Typography>
      </Paper>}

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 320, p: 2 }}>
          <Typography variant="h6" gutterBottom>Add or hide widgets</Typography>
          <List>{WIDGETS.map((widget) => {
            const item = draftLayout.find((entry) => entry.id === widget.id);
            return <ListItem key={widget.id} secondaryAction={<Stack direction="row" spacing={1} alignItems="center"><Tooltip title="Show on dashboard"><Switch size="small" inputProps={{ 'aria-label': `Show ${widget.label} on dashboard` }} checked={item?.visible !== false} onChange={(event) => updateWidget(widget.id, { visible: event.target.checked })} /></Tooltip><Tooltip title="Start expanded"><Switch size="small" inputProps={{ 'aria-label': `Start ${widget.label} expanded` }} checked={item?.startExpanded !== false} onChange={(event) => updateWidget(widget.id, { startExpanded: event.target.checked })} /></Tooltip></Stack>}>
              <ListItemText primary={widget.label} secondary={`${widget.description} · Show / Start expanded`} />
            </ListItem>;
          })}</List>
        </Box>
      </Drawer>

      {isVisible('attention') && <Paper sx={{ p: expanded.attention ? 2.5 : 1.25, mb: 3, order: orderFor('attention'), bgcolor: workspace.card, border: `1px solid ${workspace.border}`, boxShadow: workspace.shadow, borderRadius: `${workspace.radius}px`, transition: 'padding 180ms ease', '@media (prefers-reduced-motion: reduce)': { transition: 'none' } }}>
        {sectionHeader('attention', 'What needs attention')}
        <Collapse in={expanded.attention} timeout="auto" unmountOnExit>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Start with the items that could affect delivery, deadlines or confidence in the project.
        </Typography>
        <Grid container sx={{ mt: 0.5, minWidth: 0 }}>
          <Grid item xs={12} sm={6} md={4} sx={{ pr: { sm: 2 }, pb: { xs: 1.5, md: 0 } }}>
            <Stack spacing={0.7} sx={{ borderLeft: '3px solid', borderColor: 'error.main', pl: 1.5, height: '100%' }}>
              <Stack direction="row" spacing={0.75} alignItems="center"><Typography fontWeight={700}>{dashboard.delivery_risk?.critical_overdue ? `${dashboard.delivery_risk.critical_overdue} critical consent${dashboard.delivery_risk.critical_overdue === 1 ? '' : 's'} overdue` : `${dashboard.overdue_count} overdue consent${dashboard.overdue_count === 1 ? '' : 's'}`}</Typography><Chip size="small" label="Action required" color="error" /></Stack>
              <Typography variant="body2" color="text.secondary">Require action now</Typography>
              <Box><Button size="small" variant="contained" color="error" onClick={() => navigate('/consents?attention=overdue')} aria-label="Review overdue consents">Review overdue</Button></Box>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6} md={4} sx={{ px: { sm: 2 }, pb: { xs: 1.5, md: 0 }, borderLeft: { md: `1px solid ${workspace.border}` } }}>
            <Stack spacing={0.7} sx={{ borderLeft: '3px solid', borderColor: 'warning.main', pl: 1.5, height: '100%' }}>
              <Stack direction="row" spacing={0.75} alignItems="center"><Typography fontWeight={700}>{pendingReviewCount} imported records await review</Typography><Chip size="small" label="Review" color="warning" /></Stack>
              <Typography variant="body2" color="text.secondary">Held outside the register until checked</Typography>
              <Box><Button size="small" variant="outlined" color="warning" onClick={() => navigate('/data-operations?view=review-queue')} aria-label="Open imported records review queue">Open review queue</Button></Box>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={12} md={4} sx={{ pl: { md: 2 }, pt: { xs: 1.5, md: 0 }, borderLeft: { md: `1px solid ${workspace.border}` } }}>
            <Stack spacing={0.7} sx={{ borderLeft: '3px solid', borderColor: staleSourceCount ? 'warning.main' : 'success.main', pl: 1.5, height: '100%' }}>
              <Stack direction="row" spacing={0.75} alignItems="center"><Typography fontWeight={700}>{staleSourceCount ? `${staleSourceCount} source${staleSourceCount === 1 ? '' : 's'} need a successful run` : 'All configured sources are healthy'}</Typography><Chip size="small" label={staleSourceCount ? 'Check' : 'Healthy'} color={staleSourceCount ? 'warning' : 'success'} /></Stack>
              <Typography variant="body2" color="text.secondary">{staleSourceCount ? 'Check data freshness' : 'No action required'}</Typography>
              <Box><Button size="small" variant="text" color={staleSourceCount ? 'warning' : 'success'} onClick={() => navigate('/data-operations?view=health')} aria-label="View source health">View source health</Button></Box>
            </Stack>
          </Grid>
        </Grid>
        </Collapse>
      </Paper>}

      {isVisible('deliveryRisk') && <Paper sx={{ p: expanded.deliveryRisk ? 2.5 : 1.25, mb: 3, order: orderFor('deliveryRisk'), bgcolor: workspace.card, border: `1px solid ${workspace.border}`, boxShadow: workspace.shadow, borderRadius: `${workspace.radius}px`, transition: 'padding 180ms ease', '@media (prefers-reduced-motion: reduce)': { transition: 'none' } }}>
        {sectionHeader('deliveryRisk', 'Delivery risk')}
        <Collapse in={expanded.deliveryRisk} timeout="auto" unmountOnExit>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Prioritise consent work by delivery importance and deadline pressure. Compliance severity is assessed separately.</Typography>
        <Grid container spacing={2}>
          {[
            { key: 'critical', label: 'Critical risks', value: criticalRiskCount, accent: risk.critical, caption: criticalRiskCount ? 'Immediate intervention required' : 'None require intervention' },
            { key: 'high', label: 'High risks', value: highRiskCount, accent: risk.medium, caption: highRiskCount ? 'High-priority deadlines within 30 days' : 'No high-priority deadlines within 30 days' },
            { key: 'ontrack', label: 'On track', value: onTrackCount, accent: risk.low, caption: 'Outside critical and high risk bands' },
          ].map(metric => (
            <Grid item xs={12} sm={4} key={metric.key}>
              <Box sx={{ height: '100%', p: 1.75, bgcolor: workspace.cardSecondary, border: `1px solid ${workspace.border}`, borderLeft: `3px solid ${metric.value ? metric.accent : workspace.border}`, borderRadius: `${workspace.controlRadius}px` }}>
                <Typography variant="overline" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6 }}>{metric.label}</Typography>
                <Typography variant="h4" fontWeight={700} sx={{ color: metric.value ? metric.accent : 'text.disabled', lineHeight: 1.15 }}>{metric.value}</Typography>
                <Typography variant="caption" color="text.secondary">{metric.caption}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        <Stack direction="row" spacing={1} alignItems="baseline" justifyContent="space-between" flexWrap="wrap" sx={{ mt: 3, mb: 0.75 }}>
          <Typography variant="subtitle2">Delivery priority matrix</Typography>
          <Typography variant="caption" color="text.secondary">Click a populated cell to view matching consents</Typography>
        </Stack>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>{focusCount ? `${focusCount} consent${focusCount === 1 ? '' : 's'} require attention based on deadline pressure and delivery importance.` : 'No consents combine high deadline pressure with high delivery importance.'}</Typography>
        {busiestCell && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>Largest concentration: {busiestCell[1]} consent{busiestCell[1] === 1 ? '' : 's'} at importance {busiestCell[0].split(':')[1]}, {busiestCell[0].split(':')[0]} deadline pressure.</Typography>}
        <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderColor: workspace.border, bgcolor: workspace.cardSecondary, borderRadius: `${workspace.controlRadius}px`, boxShadow: 'none' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.62rem' }}>{'Delivery importance \u2192'}</Typography>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Lower risk</Typography>
              <Box sx={{ width: 56, height: 6, borderRadius: 3, background: `linear-gradient(90deg, ${risk.low}, ${risk.medium}, ${risk.high})` }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Higher risk</Typography>
            </Stack>
          </Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: '20px 68px repeat(5, minmax(46px, 1fr))', columnGap: 1, rowGap: 1, overflowX: 'auto' }} aria-label="Delivery priority matrix">
            <Box sx={{ gridColumn: 1, gridRow: '2 / span 3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="overline" color="text.secondary" sx={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: '0.62rem', whiteSpace: 'nowrap' }}>Deadline pressure</Typography>
            </Box>
            {[1,2,3,4,5].map(level => <Typography key={`importance-${level}`} variant="caption" fontWeight={700} color="text.secondary" sx={{ gridColumn: level + 2, gridRow: 1, textAlign: 'center' }}>{level}</Typography>)}
            {MATRIX_URGENCY.map((band, row) => <React.Fragment key={band.key}>
              <Typography variant="body2" fontWeight={600} sx={{ gridColumn: 2, gridRow: row + 2, alignSelf: 'center' }}>{band.label}</Typography>
              {[1,2,3,4,5].map(level => {
                const count = matrixCells[`${band.key}:${level}`] || 0;
                const share = maxCellCount ? count / maxCellCount : 0;
                const dominant = count > 0 && (share >= 0.6 || (band.key === 'high' && level >= 4));
                const zoneT = band.weight * 0.6 + ((level - 1) / 4) * 0.4;
                const zone = matrixZoneColor(zoneT);
                const bubbleSize = dominant ? 40 : share >= 0.3 ? 34 : 28;
                const cell = (
                  <ButtonBase
                    key={level}
                    disabled={!count}
                    onClick={() => count && navigate(`/consents?criticality=${level}&delivery_urgency=${band.key}`)}
                    aria-label={`${band.key} deadline pressure, delivery importance ${level}: ${count} consents`}
                    sx={{
                      gridColumn: level + 2, gridRow: row + 2, minHeight: 54, borderRadius: `${workspace.controlRadius}px`,
                      border: '1px solid', borderColor: alpha(zone, count ? 0.28 : 0.16),
                      bgcolor: alpha(zone, count ? (dominant ? 0.16 : 0.09) : 0.05),
                      display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: count ? 'pointer' : 'default',
                      transition: 'box-shadow 160ms ease, border-color 160ms ease',
                      '&:hover': count ? { boxShadow: workspace.raisedShadow, borderColor: alpha(zone, 0.5), '& .risk-bubble': { transform: 'scale(1.08)' } } : {},
                      '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
                      '&.Mui-disabled': { opacity: 1 },
                      '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                    }}
                  >
                    {count > 0 && (
                      <Box
                        className="risk-bubble"
                        sx={{
                          width: bubbleSize, height: bubbleSize, borderRadius: '50%',
                          bgcolor: dominant ? band.accent : alpha(band.accent, 0.85),
                          color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 800, fontSize: dominant ? '1rem' : share >= 0.3 ? '0.9rem' : '0.78rem', lineHeight: 1,
                          boxShadow: dominant ? workspace.raisedShadow : 'none',
                          transition: 'transform 160ms ease',
                          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                        }}
                      >
                        {count}
                      </Box>
                    )}
                  </ButtonBase>
                );
                return count > 0
                  ? <Tooltip key={level} title={`${count} consent${count === 1 ? '' : 's'} · ${band.label.toLowerCase()} deadline pressure · delivery importance ${level}`}>{cell}</Tooltip>
                  : cell;
              })}
            </React.Fragment>)}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1.25 }}>Delivery importance 1 = low · 5 = high</Typography>
        </Paper>
        </Collapse>
      </Paper>}

      <Grid container spacing={2} sx={{ mb: 3, order: Math.min(orderFor('changed'), orderFor('position')), display: customising ? 'contents' : undefined }}>
        {isVisible('changed') && <Grid item xs={12} md={6} sx={{ order: orderFor('changed') }}><Paper sx={{ p: 2, height: '100%', bgcolor: workspace.card, borderRadius: `${workspace.radius}px`, boxShadow: workspace.shadow }}>{sectionHeader('changed', 'What changed', `${dashboard.recent_consents.length} recent updates`)}<Collapse in={expanded.changed} timeout="auto" unmountOnExit><Typography variant="body2" color="text.secondary">{dashboard.recent_consents.length} consent records have recent activity. Source runs and evidence remain available in the detailed views.</Typography><Button sx={{ mt: 1 }} size="small" onClick={() => navigate('/audit-log')}>View recent activity</Button></Collapse></Paper></Grid>}
        {isVisible('position') && <Grid item xs={12} md={6} sx={{ order: orderFor('position') }}><Paper sx={{ p: 2, height: '100%', bgcolor: workspace.card, borderRadius: `${workspace.radius}px`, boxShadow: workspace.shadow }}>{sectionHeader('position', 'Project position', `${dashboard.total_consents} consents across ${connectors.length || 'configured'} sources`)}<Collapse in={expanded.position} timeout="auto" unmountOnExit><Typography variant="body2" color="text.secondary">{dashboard.total_consents} consent records are tracked across {connectors.length || 'configured'} live source connections.</Typography><Button sx={{ mt: 1 }} size="small" onClick={() => navigate('/authorities')}>View authority coverage</Button></Collapse></Paper></Grid>}
      </Grid>

      {/* KPI strip + compliance health */}
      {isVisible('health') && <Paper sx={{ p: 2, mb: 3, order: orderFor('health') }}>
        {sectionHeader('health', 'Compliance health', `${dashboard.total_consents} total consents`)}
        <Collapse in={expanded.health} timeout="auto" unmountOnExit>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 4 }} divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />} alignItems="center" sx={{ width: '100%' }}>
          {/* Health indicator */}
          <Box sx={{ minWidth: 120, textAlign: 'center' }}>
            <Tooltip title={`${healthLabel}: ${healthScore}/100. Score reduces for overdue and imminent consents.`}>
              <Box>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <svg width={72} height={72} viewBox="0 0 72 72">
                    <circle cx={36} cy={36} r={28} fill="none" stroke="#F1F3F6" strokeWidth={8} />
                    <circle cx={36} cy={36} r={28} fill="none" stroke={healthColor} strokeWidth={8}
                      strokeDasharray={`${(healthScore / 100) * 175.9} 175.9`}
                      strokeLinecap="round"
                      transform="rotate(-90 36 36)"
                    />
                    <text x={36} y={41} textAnchor="middle" fontSize={18} fontWeight={700} fill={healthColor}>{healthScore}</text>
                  </svg>
                </Box>
                <Typography variant="caption" fontWeight={700} sx={{ color: healthColor, display: 'block' }}>{healthLabel}</Typography>
                <Typography variant="caption" color="text.secondary">Compliance Health</Typography>
              </Box>
            </Tooltip>
          </Box>
          <Stack direction="row" spacing={{ xs: 1, sm: 4 }} divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />} sx={{ flex: 1, width: '100%', flexWrap: { xs: 'wrap', sm: 'nowrap' }, justifyContent: { xs: 'space-between', sm: 'flex-start' } }}>
            <Box sx={{ minWidth: 0, flex: { xs: '1 1 45%', sm: '0 1 auto' }, textAlign: { xs: 'center', sm: 'left' } }}>
              <Typography variant="h4" fontWeight={700}>{dashboard.total_consents}</Typography>
              <Typography variant="caption" color="text.secondary">Total Consents</Typography>
            </Box>
            <Box sx={{ minWidth: 0, flex: { xs: '1 1 45%', sm: '0 1 auto' }, textAlign: { xs: 'center', sm: 'left' } }}>
              <Typography variant="h4" fontWeight={700} sx={{ color: STATUS_META.approved.color }}>
                {dashboard.status_breakdown.approved || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">Approved</Typography>
            </Box>
            <Box sx={{ minWidth: 0, flex: { xs: '1 1 45%', sm: '0 1 auto' }, textAlign: { xs: 'center', sm: 'left' } }}>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#D97706' }}>{dashboard.upcoming_count}</Typography>
              <Typography variant="caption" color="text.secondary">Due Within 14 Days</Typography>
            </Box>
            <Box sx={{ minWidth: 0, flex: { xs: '1 1 45%', sm: '0 1 auto' }, textAlign: { xs: 'center', sm: 'left' } }}>
              <Typography variant="h4" fontWeight={700} sx={{ color: risk.critical }}>
                {dashboard.overdue_count}
              </Typography>
              <Typography variant="caption" color="text.secondary">Overdue — Action Required</Typography>
            </Box>
          </Stack>
        </Stack>
        </Collapse>
      </Paper>}

      <Grid container spacing={3} sx={{ mb: 3, order: Math.min(orderFor('compliance'), orderFor('categories'), orderFor('sources')), display: customising ? 'contents' : undefined }}>
        {/* Compliance position */}
        {isVisible('compliance') && <Grid item xs={12} md={4} sx={{ order: orderFor('compliance') }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            {sectionHeader('compliance', 'Compliance Position')}
            <Collapse in={expanded.compliance} timeout="auto" unmountOnExit>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{ width: 130, height: 130, flexShrink: 0 }}>
                <Doughnut data={donutData} options={{ plugins: { legend: { display: false } }, cutout: '68%' }} />
              </Box>
              <Stack spacing={0.75} sx={{ flex: 1 }}>
                {Object.entries(dashboard.status_breakdown).filter(([,v]) => v > 0).map(([key, count]) => (
                  <Stack key={key} direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: STATUS_META[key]?.color || '#94A3B8', flexShrink: 0 }} />
                      <Typography variant="body2">{STATUS_META[key]?.label || key}</Typography>
                    </Stack>
                    <Typography variant="body2" fontWeight={600}>{count}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
            </Collapse>
          </Paper>
        </Grid>}

        {/* Portfolio by category */}
        {isVisible('categories') && <Grid item xs={12} md={4} sx={{ order: orderFor('categories') }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            {sectionHeader('categories', 'Consents by Category')}
            <Collapse in={expanded.categories} timeout="auto" unmountOnExit>
            <Stack spacing={1.25}>
              {dashboard.category_breakdown
                .filter((c) => c.count > 0)
                .slice()
                .sort((a, b) => b.count - a.count)
                .map((cat) => (
                  <Box key={cat.code}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
                      <Typography variant="body2">{cat.name}</Typography>
                      <Typography variant="body2" fontWeight={600}>{cat.count}</Typography>
                    </Stack>
                    <Box sx={{ height: 5, bgcolor: '#F1F3F6', borderRadius: 0.5, overflow: 'hidden' }}>
                      <Box sx={{ height: '100%', width: `${(cat.count / maxCategoryCount) * 100}%`, bgcolor: cat.color || '#2450C6' }} />
                    </Box>
                  </Box>
                ))}
            </Stack>
            </Collapse>
          </Paper>
        </Grid>}

        {/* Authority ingestion status */}
        {isVisible('sources') && <Grid item xs={12} md={4} sx={{ order: orderFor('sources') }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            {sectionHeader('sources', 'Authority Ingestion Status')}
            <Collapse in={expanded.sources} timeout="auto" unmountOnExit>
            <Stack spacing={1}>
              {connectors.slice(0, 6).map((auth) => {
                const hasRun = !!auth.last_successful_run;
                const chip = hasRun ? { color: '#16A34A', label: 'Checked' } : { color: '#D97706', label: 'Needs check' };
                return (
                  <Stack key={auth.connector_id} direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{auth.source_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{auth.authority_code} · {auth.expected_cadence}</Typography>
                    </Box>
                    <Chip size="small" label={chip.label} sx={{ bgcolor: chip.color, color: '#fff' }} />
                  </Stack>
                );
              })}
              {!connectors.length && <Typography variant="body2" color="text.secondary">No source connections have completed a run yet.</Typography>}
            </Stack>
            </Collapse>
          </Paper>
        </Grid>}
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3, order: Math.min(orderFor('upcoming'), orderFor('risks')), display: customising ? 'contents' : undefined }}>
        {/* Upcoming obligations */}
        {isVisible('upcoming') && <Grid item xs={12} md={6} sx={{ order: orderFor('upcoming') }}>
          <Paper sx={{ height: '100%' }}>
            <Box sx={{ p: 2, pb: 0 }}>
              {sectionHeader('upcoming', 'Upcoming Obligations', 'Next 30 days')}
              {expanded.upcoming && <Typography variant="caption" color="text.secondary">Next 30 days</Typography>}
            </Box>
            <Collapse in={expanded.upcoming} timeout="auto" unmountOnExit>
            <TableContainer sx={{ overflowX: 'auto', '& table': { minWidth: 460 } }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Reference</TableCell>
                    <TableCell>Authority</TableCell>
                    <TableCell>Due</TableCell>
                    <TableCell align="right">Days</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {upcoming.length === 0 && (
                    <TableRow><TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No obligations due in the next 30 days.</Typography>
                    </TableCell></TableRow>
                  )}
                  {upcoming.map((c) => (
                    <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/consents/${c.id}`)}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '0.8rem' }}>{c.reference}</Typography>
                        <Typography variant="caption" color="text.secondary">{c.consent_type?.name}</Typography>
                      </TableCell>
                      <TableCell><Typography variant="body2">{c.cgb?.name || '—'}</Typography></TableCell>
                      <TableCell><Typography variant="body2">{format(new Date(c.target_approval_date as string), 'dd MMM')}</Typography></TableCell>
                      <TableCell align="right">
                        <Chip size="small" label={`${c.days}d`} sx={{ bgcolor: c.days <= 7 ? '#FEF3C7' : '#F1F3F6', color: c.days <= 7 ? '#92400E' : 'text.primary' }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </Collapse>
          </Paper>
        </Grid>}

        {/* Risks requiring attention */}
        {isVisible('risks') && <Grid item xs={12} md={6} sx={{ order: orderFor('risks') }}>
          <Paper sx={{ height: '100%' }}>
            <Box sx={{ p: 2, pb: 0 }}>
              {sectionHeader('risks', 'Overdue Compliance Obligations', `${risks.length} overdue obligations`)}
              {expanded.risks && <Typography variant="caption" color="text.secondary">Overdue consents by severity</Typography>}
            </Box>
            <Collapse in={expanded.risks} timeout="auto" unmountOnExit>
            <TableContainer sx={{ overflowX: 'auto', '& table': { minWidth: 460 } }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Reference</TableCell>
                    <TableCell>Authority</TableCell>
                    <TableCell>Compliance severity</TableCell>
                    <TableCell align="right">Overdue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {risks.length === 0 && (
                    <TableRow><TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No overdue consents — project is current.</Typography>
                    </TableCell></TableRow>
                  )}
                  {risks.map((c) => {
                    const sev = severityFor(c.days);
                    return (
                      <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/consents/${c.id}`)}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '0.8rem' }}>{c.reference}</Typography>
                          <Typography variant="caption" color="text.secondary">{c.consent_type?.name}</Typography>
                        </TableCell>
                        <TableCell><Typography variant="body2">{c.cgb?.name || '—'}</Typography></TableCell>
                        <TableCell><Chip size="small" label={sev.label} sx={{ bgcolor: sev.color, color: '#fff' }} /></TableCell>
                        <TableCell align="right"><Typography variant="body2" fontWeight={600} sx={{ color: sev.color }}>{Math.abs(c.days)}d</Typography></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            </Collapse>
          </Paper>
        </Grid>}
      </Grid>

      {/* Recent regulatory activity */}
      {isVisible('activity') && <Paper sx={{ p: 2, mb: 3, order: orderFor('activity') }}>
        {sectionHeader('activity', 'Recent Regulatory Activity', `${dashboard.recent_consents.length} recent updates`)}
        <Collapse in={expanded.activity} timeout="auto" unmountOnExit>
        <Stack divider={<Divider />}>
          {dashboard.recent_consents.map((c) => (
            <Stack key={c.id} direction="row" alignItems="center" spacing={1.5} sx={{ py: 1.25, cursor: 'pointer' }} onClick={() => navigate(`/consents/${c.id}`)}>
              {ACTIVITY_ICON[c.status] || <DotIcon fontSize="small" />}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2">
                  <strong>{c.reference}</strong> — {c.consent_type}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDistanceToNow(new Date(c.updated_at), { addSuffix: true })}
                </Typography>
              </Box>
              <Chip size="small" label={STATUS_META[c.status]?.label || c.status} sx={{ bgcolor: STATUS_META[c.status]?.color, color: '#fff' }} />
              {c.status_percentage > 0 && c.status_percentage < 100 && (
                <Box sx={{ width: 60 }}>
                  <LinearProgress variant="determinate" value={c.status_percentage} sx={{ height: 4, borderRadius: 2 }} />
                </Box>
              )}
            </Stack>
          ))}
          {!dashboard.recent_consents.length && <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No consent records have changed recently. New activity will appear here after the next update.</Typography>}
        </Stack>
        </Collapse>
      </Paper>}
    </Box>
  );
};

export default Dashboard;
